/**
 * iframe高度自适应Hook
 * 用于在iframe中嵌入的页面自动调整父窗口iframe的高度
 */

import { useEffect, useRef } from 'react';

interface UseIframeHeightSyncOptions {
  /**
   * 最小高度（默认为半屏高度）
   */
  minHeight?: number;
  /**
   * 是否启用（默认true）
   */
  enabled?: boolean;
  /**
   * 防抖延迟时间（毫秒，默认100ms）
   */
  debounceDelay?: number;
  /**
   * 额外的高度偏移量（默认50px，用于避免出现滚动条）
   */
  heightOffset?: number;
}

/**
 * 检测当前页面是否在iframe中
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

/**
 * 获取半屏高度
 */
function getHalfScreenHeight(): number {
  return Math.floor(window.screen.height / 2);
}

/**
 * 获取页面实际内容高度
 */
function getPageHeight(): number {
  // 使用document.body.scrollHeight作为主要依据
  // 这是实际内容的高度，不包括viewport的额外空间
  return document.body.scrollHeight || document.documentElement.scrollHeight;
}

/**
 * 向父窗口发送高度调整消息
 */
function notifyParentHeight(height: number): void {
  try {
    window.parent.postMessage(
      {
        methodName: 'height',
        params: { height },
        messageId: Date.now(),
      },
      '*'
    );
    console.log(`📐 已发送iframe高度调整请求: ${height}px`);
  } catch (error) {
    console.warn('⚠️ 调整iframe高度失败', error);
  }
}

/**
 * iframe高度自适应Hook
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   useIframeHeightSync({
 *     minHeight: 400,
 *     enabled: true,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useIframeHeightSync(options: UseIframeHeightSyncOptions = {}) {
  const {
    minHeight,
    enabled = true,
    debounceDelay = 300,
    heightOffset = 20,
  } = options;

  const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeightRef = useRef<number>(0);
  const syncCountRef = useRef<number>(0);
  const isAnimatingRef = useRef<boolean>(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 如果未启用或不在iframe中，直接返回
    if (!enabled || !isInIframe()) {
      return;
    }

    /**
     * 计算并同步高度
     */
    const syncHeight = () => {
      // 如果正在动画中，跳过同步（避免动画期间频繁触发）
      if (isAnimatingRef.current) {
        return;
      }

      // 获取页面实际高度
      const pageHeight = getPageHeight();

      // 获取最小高度（默认为半屏高度）
      const calculatedMinHeight = minHeight ?? getHalfScreenHeight();

      // 取实际高度和最小高度的较大值，并加上偏移量
      const targetHeight = Math.max(pageHeight, calculatedMinHeight) + heightOffset;

      // 如果高度变化小于30px，不发送消息（避免频繁通信和鬼畜）
      if (Math.abs(targetHeight - lastHeightRef.current) < 30) {
        return;
      }

      // 限制同步频率，避免无限循环
      syncCountRef.current++;
      if (syncCountRef.current > 30) {
        console.warn('⚠️ iframe高度同步次数过多，已停止同步');
        return;
      }

      lastHeightRef.current = targetHeight;
      notifyParentHeight(targetHeight);
    };

    /**
     * 防抖处理的高度同步
     */
    const debouncedSyncHeight = () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }

      resizeTimerRef.current = setTimeout(() => {
        syncHeight();
      }, debounceDelay);
    };

    /**
     * 检测动画开始
     */
    const handleAnimationStart = () => {
      isAnimatingRef.current = true;

      // 清除之前的动画超时
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      // 动画结束后1秒再同步高度
      animationTimeoutRef.current = setTimeout(() => {
        isAnimatingRef.current = false;
        syncHeight();
      }, 1000);
    };

    /**
     * 检测动画结束
     */
    const handleAnimationEnd = () => {
      // 清除之前的动画超时
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      // 动画结束后500ms再同步高度
      animationTimeoutRef.current = setTimeout(() => {
        isAnimatingRef.current = false;
        syncHeight();
      }, 500);
    };

    // 初始化时延迟同步（等待初始动画完成）
    setTimeout(() => {
      syncHeight();
    }, 800);

    // 监听窗口大小变化
    window.addEventListener('resize', debouncedSyncHeight);

    // 监听动画事件
    document.body.addEventListener('animationstart', handleAnimationStart);
    document.body.addEventListener('animationend', handleAnimationEnd);
    document.body.addEventListener('transitionstart', handleAnimationStart);
    document.body.addEventListener('transitionend', handleAnimationEnd);

    // 使用MutationObserver监听DOM变化（限制观察范围）
    const observer = new MutationObserver((mutations) => {
      // 过滤掉样式变化（通常是动画引起的）
      const hasStructuralChange = mutations.some(mutation =>
        mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
      );

      if (hasStructuralChange) {
        debouncedSyncHeight();
      }
    });

    // 只观察body的子节点变化，不观察所有属性变化
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // 不观察属性变化，减少触发频率
      attributes: false,
      characterData: false,
    });

    // 使用ResizeObserver监听body尺寸变化（更精确）
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        // 只在非动画期间触发
        if (!isAnimatingRef.current) {
          debouncedSyncHeight();
        }
      });
      resizeObserver.observe(document.body);
    }

    // 清理函数
    return () => {
      window.removeEventListener('resize', debouncedSyncHeight);
      document.body.removeEventListener('animationstart', handleAnimationStart);
      document.body.removeEventListener('animationend', handleAnimationEnd);
      document.body.removeEventListener('transitionstart', handleAnimationStart);
      document.body.removeEventListener('transitionend', handleAnimationEnd);
      observer.disconnect();
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [enabled, minHeight, debounceDelay, heightOffset]);
}
